import React, { useState } from 'react';
import { X, MessageSquare, Loader2 } from 'lucide-react';
import { apiClient } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface FeedbackModalProps {
  onClose: () => void;
}

const SUBJECT_MAX = 200;
const MESSAGE_MAX = 5000;

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ onClose }) => {
  useBodyScrollLock();
  const navigate = useNavigate();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ subject?: string; message?: string }>({});
  const [success, setSuccess] = useState(false);

  const validate = (): boolean => {
    const errors: { subject?: string; message?: string } = {};
    if (!subject.trim()) {
      errors.subject = 'Subject is required';
    } else if (subject.length > SUBJECT_MAX) {
      errors.subject = `Subject must be ${SUBJECT_MAX} characters or less`;
    }
    if (!message.trim()) {
      errors.message = 'Message is required';
    } else if (message.length > MESSAGE_MAX) {
      errors.message = `Message must be ${MESSAGE_MAX} characters or less`;
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    if (!validate()) return;

    try {
      setIsLoading(true);
      await apiClient.sendFeedback({ subject: subject.trim(), message: message.trim() });
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      const status = err.response?.status;
      if (status === 401) {
        onClose();
        navigate('/login', { replace: true });
        return;
      }
      if (status === 422) {
        const detail = err.response?.data?.detail;
        if (Array.isArray(detail)) {
          const mapped: { subject?: string; message?: string } = {};
          for (const item of detail) {
            const field = item.loc?.[item.loc.length - 1];
            if (field === 'subject') mapped.subject = item.msg;
            if (field === 'message') mapped.message = item.msg;
          }
          if (Object.keys(mapped).length > 0) {
            setFieldErrors(mapped);
            return;
          }
        }
        setError(typeof detail === 'string' ? detail : 'Validation error. Please check your input.');
        return;
      }
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 flex items-center justify-center rounded-full bg-blue-100">
              <MessageSquare className="h-5 w-5 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Send Feedback</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            title="Close"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 text-red-700 p-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-50 text-green-700 p-3 rounded-md text-sm">
              Feedback sent successfully. Thank you!
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Subject */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="feedback-subject" className="block text-sm font-medium text-gray-700">
                  Subject
                </label>
                <span className={`text-xs ${subject.length > SUBJECT_MAX ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                  {subject.length}/{SUBJECT_MAX}
                </span>
              </div>
              <input
                id="feedback-subject"
                type="text"
                value={subject}
                onChange={(e) => {
                  setSubject(e.target.value);
                  if (fieldErrors.subject) setFieldErrors((prev) => ({ ...prev, subject: undefined }));
                }}
                disabled={isLoading || success}
                maxLength={SUBJECT_MAX}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                  fieldErrors.subject ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                }`}
                placeholder="What is this about?"
              />
              {fieldErrors.subject && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.subject}</p>
              )}
            </div>

            {/* Message */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="feedback-message" className="block text-sm font-medium text-gray-700">
                  Message
                </label>
                <span className={`text-xs ${message.length > MESSAGE_MAX ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                  {message.length}/{MESSAGE_MAX}
                </span>
              </div>
              <textarea
                id="feedback-message"
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  if (fieldErrors.message) setFieldErrors((prev) => ({ ...prev, message: undefined }));
                }}
                disabled={isLoading || success}
                maxLength={MESSAGE_MAX}
                rows={5}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y disabled:opacity-50 disabled:cursor-not-allowed ${
                  fieldErrors.message ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                }`}
                placeholder="Tell us what's on your mind..."
              />
              {fieldErrors.message && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.message}</p>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || success}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                    Sending...
                  </div>
                ) : (
                  'Submit'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
